import i18n from 'i18next';
import { makeAutoObservable, runInAction } from 'mobx';

import {
  CustomerRequests,
  type RatingDetails,
  type ReviewMediaKind,
  type ReviewMediaMimeType,
  type ReviewsResponse,
} from '~/api/Customer';
import { uploadToS3 } from '~/api/uploadToS3';
import { company } from '~/company/Company';
import { AnalyticsEvent, analyticsService } from '~/services/analytics';
import { mainStore } from '~/stores';
import type { CreateReviewData } from '~/stores/ReviewsStore/interfaces';
import { Pagination } from '~/stores/shared/Pagination';
import { Task } from '~/stores/shared/Task';
import type { ProductReview } from '~/types/Product/interface';
import { PreloadCache } from '~/utils/preloadCache';

const detectMediaKind = (file: File): ReviewMediaKind =>
  file.type.startsWith('video/') ? 'video' : 'image';

type ReviewsMetadata = Omit<
  Awaited<ReturnType<typeof CustomerRequests.requestReviewsByProductId>>,
  'data' | 'pagination'
>;

const PAGE_SIZE = 10;
const defaultDetails: RatingDetails = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };

export class ReviewsStore {
  public id: number | string;

  private meta: Nullable<ReviewsMetadata> = null;

  constructor(productId: number | string) {
    this.id = productId;

    makeAutoObservable(this);

    ReviewsStore.currentInstance = this;
  }

  private static readonly preloadedData = new PreloadCache<ReviewsResponse>({
    max: 500,
    ttlMs: 60_000,
  });

  public static async preload(id: number) {
    const data = await CustomerRequests.requestReviewsByProductId({
      productId: id,
      page: { current: 1, size: PAGE_SIZE },
    });

    this.preloadedData.set(String(id), data);

    return data;
  }

  public static removePreloadedData(id: number) {
    this.preloadedData.delete(String(id));
  }

  public static currentInstance: Nullable<ReviewsStore> = null;

  public get list(): ProductReview[] {
    return this.request.result || [];
  }

  public get details(): RatingDetails {
    return this.meta?.details || defaultDetails;
  }

  public get canCreateReview(): boolean {
    return this.meta?.canCreateReview || false;
  }

  public setId(id: number) {
    this.id = id;
    const isSSR = typeof window === 'undefined';
    const initialState = isSSR
      ? ReviewsStore.preloadedData.get(String(id))
      : window.__INITIAL_STATE__?.reviews;

    if (initialState) {
      const { data, pagination, canCreateReview, details } = initialState;

      this.meta = { details, canCreateReview };
      this.request.setState({ data, pagination });

      if (!isSSR) {
        delete window.__INITIAL_STATE__?.reviews;
      }
    } else if (!isSSR && company.useReviewsOnProductPage) {
      this.request.loadPage(1);
    }
  }

  public readonly request = new Pagination(async (page) => {
    const { data, details, pagination, canCreateReview } =
      await CustomerRequests.requestReviewsByProductId({
        productId: this.id,
        page: { current: page, size: PAGE_SIZE },
      });

    runInAction(() => {
      this.meta = { details, canCreateReview };
    });

    return { data, pagination };
  });

  public readonly createReview = new Task(async (params: CreateReviewData) => {
    try {
      const mediaKeys = await this.uploadReviewMedia(
        params.photos ?? [],
        params.productId,
        params.sku,
      );

      const { data: newReview } = await CustomerRequests.addNewReview({
        productId: params.productId,
        mark: params.mark,
        content: params.content,
        sku: params.sku,
        mediaKeys: mediaKeys.length > 0 ? mediaKeys : undefined,
      });

      runInAction(() => {
        this.request.prepend(newReview);

        if (this.meta) {
          this.meta.canCreateReview = false;
        }
      });

      analyticsService.track(AnalyticsEvent.RATE_PRODUCT_DETAILED, {
        productId: params.productId,
        mark: params.mark,
        content: params.content,
      });

      return newReview;
    } catch (error) {
      if (!params.silent) {
        mainStore.pushAlert('error', i18n.t('errors:unknown'));
      }
      throw error;
    }
  });

  /**
   * Requests pre-signed upload tokens, PUTs each file body to S3 in parallel,
   * and returns the corresponding mediaKeys in the original file order.
   * Returns an empty array when there are no files to upload.
   */
  private async uploadReviewMedia(
    photos: File[],
    productId: number,
    sku?: string,
  ): Promise<string[]> {
    if (photos.length === 0) return [];

    const { data: tokens } = await CustomerRequests.requestReviewMediaTokens({
      files: photos.map((file) => ({
        mimeType: file.type as ReviewMediaMimeType,
        fileSize: file.size,
        mediaType: detectMediaKind(file),
      })),
      productId,
      sku,
    });

    await Promise.all(
      tokens.map((token, index) => {
        const file = photos[index];
        if (!file) {
          throw new Error('Mismatch between requested tokens and files');
        }
        return uploadToS3(token.uploadUrl, file);
      }),
    );

    return tokens.map((t) => t.mediaKey);
  }
}
